import { useState, useEffect, useRef, useCallback } from 'react';
import { useDataCache } from '../contexts/DataCacheContext';
import { realtimeSyncService } from '../services/realtimeSyncService';
import { logger } from '../utils/logger';

type TableName = 'contacts' | 'leads' | 'companies' | 'meetings' | 'activities' | 'users';

interface UseDataLoaderOptions {
  enabled?: boolean;
  ttl?: number;
  skipCache?: boolean;
  realtimeTable?: TableName;
  realtimeEnabled?: boolean;
  staleWhileRevalidate?: boolean;
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
  onRealtimeEvent?: (event: any) => void;
}

interface UseDataLoaderResult<T> {
  data: T | null;
  loading: boolean;
  isRevalidating: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  invalidate: () => void;
  isRealtimeConnected: boolean;
}

const pendingRequests = new Map<string, Promise<any>>();

export function useDataLoader<T>(
  key: string,
  fetchFn: () => Promise<T>,
  options: UseDataLoaderOptions = {}
): UseDataLoaderResult<T> {
  const {
    enabled = true,
    ttl = 30,
    skipCache = false,
    realtimeTable,
    realtimeEnabled = true,
    staleWhileRevalidate = true,
    onSuccess,
    onError,
    onRealtimeEvent
  } = options;

  const cache = useDataCache();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const isMountedRef = useRef(true);
  const lastFetchRef = useRef<number>(0);
  const realtimeUnsubscribeRef = useRef<(() => void) | null>(null);

  const loadData = useCallback(async (isBackground = false) => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const now = Date.now();
    const timeSinceLastFetch = (now - lastFetchRef.current) / 1000;

    if (timeSinceLastFetch < 5) {
      if (import.meta.env.DEV) console.log(`⏭️ SKIP: ${key} - Too soon since last fetch (${timeSinceLastFetch.toFixed(1)}s)`);
      return;
    }

    try {
      if (!isBackground) {
        setLoading(true);
      } else {
        setIsRevalidating(true);
        cache.setRevalidating(key, true);
      }
      setError(null);

      let dataPromise = pendingRequests.get(key);

      if (!dataPromise) {
        logger.verbose(`LOAD: ${key} - Starting new fetch`);
        dataPromise = fetchFn();
        pendingRequests.set(key, dataPromise);

        dataPromise.finally(() => {
          pendingRequests.delete(key);
        });
      } else {
        logger.verbose(`DEDUP: ${key} - Using pending request`);
      }

      const result = await dataPromise;
      lastFetchRef.current = Date.now();

      if (isMountedRef.current) {
        setData(result);
        cache.set(key, result);
        onSuccess?.(result);
        logger.verbose(`SUCCESS: ${key} - Data loaded and cached`);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');

      if (isMountedRef.current) {
        setError(error);
        onError?.(error);
        console.error(`❌ ERROR: ${key}`, error);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setIsRevalidating(false);
        cache.setRevalidating(key, false);
      }
    }
  }, [key, fetchFn, enabled, cache, onSuccess, onError]);

  const refetch = useCallback(async () => {
    await loadData(false);
  }, [loadData]);

  const invalidate = useCallback(() => {
    cache.invalidate(key);
    lastFetchRef.current = 0;
  }, [cache, key]);

  useEffect(() => {
    isMountedRef.current = true;

    const initializeData = async () => {
      if (!enabled) {
        setLoading(false);
        return;
      }

      if (!skipCache && staleWhileRevalidate) {
        const cached = cache.get<T>(key);

        if (cached) {
          logger.verbose(`CACHE HIT: ${key} - Using cached data (stale-while-revalidate)`);
          setData(cached.data);
          setLoading(false);

          if (!cache.isFresh(key, ttl)) {
            logger.verbose(`STALE: ${key} - Revalidating in background`);
            await loadData(true);
          } else {
            logger.verbose(`FRESH: ${key} - No revalidation needed`);
          }
          return;
        } else {
          logger.debug(`CACHE MISS: ${key} - Loading from source`);
        }
      }

      await loadData(false);
    };

    initializeData();

    if (realtimeTable && realtimeEnabled) {
      logger.debug(`Setting up Realtime subscription for ${key} on table ${realtimeTable}`);

      const unsubscribe = realtimeSyncService.subscribe(realtimeTable, (event) => {
        logger.debug(`🔔 Realtime event received for ${key}:`, event.type, event.record?.id);

        onRealtimeEvent?.(event);

        // For INSERT and UPDATE events, try incremental update instead of full reload
        if ((event.type === 'INSERT' || event.type === 'UPDATE') && event.record && data) {
          try {
            // If data is an array, try to update incrementally
            if (Array.isArray(data)) {
              const recordId = event.record.id;
              const existingIndex = data.findIndex((item: any) => item?.id === recordId);

              if (event.type === 'INSERT' && existingIndex === -1) {
                // Add new record to the beginning of the array
                if (import.meta.env.DEV) console.log(`➕ Incremental INSERT: Adding new record ${recordId} to ${key}`);
                const updatedData = [event.record, ...data] as T;
                setData(updatedData);
                cache.set(key, updatedData);
                return; // Skip full reload
              } else if (event.type === 'UPDATE' && existingIndex !== -1) {
                // Update existing record in place
                logger.verbose(`Incremental UPDATE: Updating record ${recordId} in ${key}`);
                const updatedData = [...data];
                updatedData[existingIndex] = event.record;
                setData(updatedData as T);
                cache.set(key, updatedData as T);
                return; // Skip full reload
              }
            }
          } catch (err) {
            console.warn(`⚠️ Incremental update failed for ${key}, falling back to full reload:`, err);
          }
        }

        // For DELETE events or if incremental update failed, invalidate and reload
        cache.invalidate(key);
        loadData(true);
      });

      realtimeUnsubscribeRef.current = unsubscribe;

      setIsRealtimeConnected(realtimeSyncService.isConnected(realtimeTable));

      const checkConnectionInterval = setInterval(() => {
        setIsRealtimeConnected(realtimeSyncService.isConnected(realtimeTable));
      }, 5000);

      return () => {
        isMountedRef.current = false;
        unsubscribe();
        clearInterval(checkConnectionInterval);
      };
    }

    return () => {
      isMountedRef.current = false;
      if (realtimeUnsubscribeRef.current) {
        realtimeUnsubscribeRef.current();
      }
    };
  }, [key, enabled, skipCache, ttl, cache, loadData, realtimeTable, realtimeEnabled, staleWhileRevalidate, onRealtimeEvent]);

  return {
    data,
    loading,
    isRevalidating,
    error,
    refetch,
    invalidate,
    isRealtimeConnected
  };
}
